// Purpose: admin system settings — feature flags, geo rules, config readback.

import { api, csrfHeaders } from './client.ts';

export interface FeatureFlag {
  key: string;
  enabled: boolean;
  description: string;
  updatedAt: string;
  updatedBy: string | null;
}

export interface GeoRules {
  allowedCountries: string[];
  blockedCountries: string[];
  anomalyThreshold: {
    distinctCountries: number;
    windowMinutes: number;
  };
}

export interface SafeConfigView {
  AST_ENV: string;
  AST_API_VERSION: string;
  AST_SCANNER_TYPE: string;
  AST_GEOIP_TYPE: string;
  AST_GRAPH_TYPE: string;
  AST_QUEUE_TYPE: string;
  AST_MFA_MAX_AGE_MINUTES: number;
  AST_BREAK_GLASS_SESSION_MINUTES: number;
  AST_SHARE_MAX_TTL_HOURS: number;
  AST_AUDIT_RETENTION_DAYS: number;
  AST_ROUTING_MAX_ATTEMPTS: number;
  AST_NOTIFICATION_RATE_LIMIT_PER_HOUR: number;
}

export async function fetchConfig(): Promise<SafeConfigView> {
  const res = await api.get<SafeConfigView>('/admin/settings/config');
  return res.data;
}

export async function listFeatureFlags(): Promise<FeatureFlag[]> {
  const res = await api.get<{ flags: FeatureFlag[] }>('/admin/settings/feature-flags');
  return res.data.flags;
}

export async function setFeatureFlag(
  key: string,
  enabled: boolean,
  description?: string
): Promise<FeatureFlag> {
  const res = await api.post<FeatureFlag>(
    `/admin/settings/feature-flags/${encodeURIComponent(key)}`,
    { enabled, ...(description ? { description } : {}) },
    { headers: csrfHeaders() }
  );
  return res.data;
}

export async function fetchGeoRules(dropboxId: string): Promise<GeoRules | null> {
  const res = await api.get<{ rules: GeoRules | null }>(
    `/admin/settings/geo-rules/${encodeURIComponent(dropboxId)}`
  );
  return res.data.rules;
}

export async function setGeoRules(dropboxId: string, rules: GeoRules): Promise<void> {
  await api.put(`/admin/settings/geo-rules/${encodeURIComponent(dropboxId)}`, rules, {
    headers: csrfHeaders(),
  });
}

export async function clearGeoRules(dropboxId: string): Promise<void> {
  await api.delete(`/admin/settings/geo-rules/${encodeURIComponent(dropboxId)}`, {
    headers: csrfHeaders(),
  });
}
